<template>
  <div class="audience-filter-selector">
    <div class="selector-left">
      <aside class="menu">
        <ul class="menu-list">
          <li v-for="(item, index) in menuItems" :key="index" @click="toggleMenuItem(item)">
            <a :class="getMenuItemClass(item)">
              {{ $t(`audience.filter.selector.menu.${item}`) }}
              <span
                class="tag is-rounded"
                v-if="activeFilters[item] > 0"
              >{{ activeFilters[item] }}</span>
            </a>
          </li>
        </ul>
      </aside>
    </div>
    <div class="selector-center">
      <div
        class="types"
        v-masonry
        transition-duration="0"
        column-width=".audience-filter-category"
        item-selector=".audience-filter-category"
      >
        <audience-filter-category
          v-masonry-tile
          v-for="(value, key) in items"
          :key="key"
          :type="activeMenuItem"
          :items="value"
          :category="key"
        />
      </div>
    </div>
    <div class="selector-right">
      <div class="selected-filters">
        <div
          v-if="filters.length === 0"
          class="no-filters"
        >{{ $t('audience.filter.selector.no_filters_selected') }}</div>
        <div v-else class="field is-grouped is-grouped-multiline">
          <div class="control" v-for="(item, index) in filters" :key="index">
            <div class="tags has-addons" :class="{ 'is-hovered': item === hoveredSelectedFilter }">
              <span class="tag">{{ getDisplayName(item) }}</span>
              <a
                class="tag is-delete"
                @click="removeFilter(item)"
                @mouseover="mouseoverSelectedFilter(item)"
                @mouseleave="mouseleaveSelectedFilter(item)"
              >
                <i class="material-icons">&#xE5CD;</i>
              </a>
            </div>
          </div>
          <div class="control">
            <div class="tags has-addons clear">
              <span class="tag">{{ $t('audience.filter.selector.clear') }}</span>
              <a class="tag is-delete" @click="clearFilters()">
                <i class="material-icons">&#xE148;</i>
              </a>
            </div>
          </div>
        </div>
      </div>
      <div class="buttons has-addons">
        <div class="button is-white" @click="close()">
          <span class="icon is-small">
            <i class="material-icons">&#xE5CD;</i>
          </span>
          <span>{{ $t('ui.buttons.close') }}</span>
        </div>
        <div class="button is-dark" :class="{ 'is-disabled': !isFilterModified }" @click="apply()">
          <span class="icon is-small">
            <i class="material-icons">&#xE5CA;</i>
          </span>
          <span>{{ $t('ui.buttons.apply') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { map, filter, reduce, omit, values, isEqual } from "lodash";
import { mapState, mapGetters } from "vuex";
import {
  transportTypes,
  stationaryTypes,
  getEventKeypath
} from "config/settings";
import AudienceFilterCategory from "./AudienceFilterCategory";
import { humanize } from "utils/string";

export default {
  name: "AudienceFilterSelector",

  components: {
    AudienceFilterCategory
  },

  data() {
    return {
      activeMenuItem: "events",
      hoveredSelectedFilter: null,
      menuItems: ["events", "moments", "segments"]
    };
  },

  computed: {
    ...mapState("audience", ["filters", "appliedFilters"]),

    ...mapGetters("account", ["momentDefinitions", "segmentDefinitions"]),

    items() {
      switch (this.activeMenuItem) {
        case "events":
          return this.events;
        case "moments":
          return this.moments;
        case "segments":
          return this.segments;
        default:
          return [];
      }
    },

    events() {
      return {
        stationary_locations: stationaryTypes,
        transport_modes: transportTypes
      };
    },

    moments() {
      return this.groupBy(values(this.momentDefinitions), "category");
    },

    segments() {
      let groupedSegmentDefinitions = this.groupBy(
        values(this.segmentDefinitions),
        "subcategory"
      );

      groupedSegmentDefinitions = omit(groupedSegmentDefinitions, "geography");

      return groupedSegmentDefinitions;
    },

    isFilterModified() {
      return !isEqual(this.filters, this.appliedFilters);
    },

    activeFilters() {
      const events = this.getFilterTypeCount("events");
      const moments = this.getFilterTypeCount("moments");
      const segments = this.getFilterTypeCount("segments");

      return { events, moments, segments };
    }
  },

  methods: {
    toDefinitionFormat(item) {
      return {
        id: item,
        display_name: item
      };
    },

    groupBy(values, key) {
      const groupedValues = reduce(
        values,
        (acc, curr) => {
          const keyValue = curr[key];

          if (!acc[keyValue]) {
            acc[keyValue] = [];
          }

          acc[keyValue] = [...acc[keyValue], curr];

          return acc;
        },
        {}
      );

      return groupedValues;
    },

    toggleMenuItem(id) {
      this.activeMenuItem = id;
    },

    getMenuItemClass(id) {
      return {
        "is-active": id === this.activeMenuItem
      };
    },

    getDisplayName(item) {
      const { type, id } = item;

      if (type === "events") {
        const keypath = getEventKeypath(id);

        return this.$t(keypath);
      }

      if (type === "moments") {
        return this.momentDefinitions[id].display_name;
      }

      if (type === "segments") {
        return this.segmentDefinitions[id].display_name;
      }

      return id;
    },

    getFilterTypeCount(type) {
      return filter(this.filters, item => item.type === type).length;
    },

    removeFilter(item) {
      this.$store.commit("audience/removeFilterItem", item);
    },

    clearFilters() {
      this.$store.commit("audience/clearFilters");
    },

    close() {
      this.$store.commit("audience/resetFilters");
      this.$emit("close-filter");
    },

    apply() {
      if (!this.isFilterModified) {
        return;
      }

      console.log("Applying filters...");

      this.$store.commit("audience/applyFilters");
      this.$emit("close-filter");
    },

    mouseoverSelectedFilter(item) {
      this.hoveredSelectedFilter = item;
    },

    mouseleaveSelectedFilter(item) {
      this.hoveredSelectedFilter = null;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~scss/utilities/initial-variables.scss";
@import "~scss/utilities/derived-variables.scss";
@import "~scss/utilities/mixins.scss";

.audience-filter-selector {
  position: absolute;
  display: flex;
  left: 0;
  top: 100%;
  width: 60rem;
  height: 28.75rem;
  // background-color: $white;
  transform: translateY(8px);
  z-index: 20;
  @include diffused-drop-shadow();
}

.selector-left {
  flex: 0 0 10rem;
  background-color: #f0f0f0;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  overflow: hidden;
}

.selector-center {
  flex: 1;
  background-color: $white;
  overflow-x: hidden;
  overflow-y: scroll;
}

.selector-right {
  display: flex;
  flex: 0 0 16.625rem;
  flex-direction: column;
  background-color: #0f0f0f;
  // background: linear-gradient(to bottom, #343434 0%,#262626 100%);
  background: linear-gradient(to bottom, #f7f7f7 0%, #e9e9e9 100%);
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  overflow: hidden;

  .buttons {
    display: flex;
    margin-bottom: 0;

    &.has-addons {
      .button {
        &:not(:last-child) {
          margin-right: 0;
        }
      }
    }

    .button {
      flex: 1;
      justify-content: center;
      padding-top: 0.6875rem;
      padding-bottom: 0.6875rem;
      margin-bottom: 0;
      font-size: $size-small;
      font-weight: $weight-medium;
      letter-spacing: 2.5px;
      text-transform: uppercase;
      transition: $transition;

      &:first-child {
        border-left: solid 1px rgba($black, 0.08);
      }

      &.is-dark {
        // background-color: rgba($white, .2);
        // color: rgba($white, .87);
        background-color: #4a4a4a;
        color: rgba($white, 0.87);

        &.is-disabled {
          // color: rgba($white, .24);
          // background-color: rgba($white, .12);
          background-color: rgba(#4a4a4a, 0.32);
        }
      }

      &.is-white {
        color: rgba($black, 0.87);
      }

      &.is-disabled {
        cursor: default;
      }

      .icon {
        &:first-child {
          &:not(:last-child) {
            margin-right: 0.375rem;
          }
        }
      }
    }
  }
}

.types {
  margin: 1.5rem 2rem;

  .audience-filter-category {
    width: 50%;
  }
}

.selected-filters {
  flex: 1;
  padding: 1rem;
  padding-left: 1.5rem;

  .no-filters {
    font-size: 0.875rem;
    // color: rgba($white, .87);
    color: rgba($black, 0.87);
  }

  .field {
    &.is-grouped {
      > .control {
        margin-right: 0.25rem;
        margin-bottom: 0.25rem;

        &:last-child {
          // margin-top: .5rem;
          width: 100%;
        }
      }
    }
  }

  .tags {
    margin-bottom: 0rem;
    box-shadow: 0 1px 2px 0 rgba($black, 0.12);
    transition: $transition;

    &.has-addons {
      .tag {
        margin-bottom: 0rem;

        &:not(:first-child) {
          padding-left: 0.125rem;
          border-left-width: 0;
        }

        &:not(:last-child) {
          padding-right: 0.125rem;
          border-right-width: 0;
        }
      }
    }

    &.is-hovered {
      box-shadow: 0 1px 2px 0 rgba($black, 0.24);

      .tag {
        // border-color: rgba($white, .4)
      }
    }

    &.clear {
      box-shadow: none;
      margin-top: 0.5rem;

      .tag {
        background-color: rgba($white, 0.1);
        border: 1px solid rgba($black, 0.12);

        &.is-delete {
          &:hover,
          &:focus,
          &:active {
            background-color: rgba($white, 0.1);
          }

          i {
            font-size: 0.875rem;
            transform: rotate(45deg);
          }
        }
      }
    }
  }

  .tag {
    background-color: transparent;
    height: 1.5rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    font-size: 0.875rem;
    line-height: 1;
    // color: rgba($white, .87);
    // border: 1px solid rgba($white, .16);
    color: rgba($black, 0.87);
    background-color: $white;
    text-transform: lowercase;
    transition: $transition;

    &.is-delete {
      width: 1.5rem;
      margin-left: 0;
      padding-left: 0.375rem;
      padding-right: 0.375rem;
      text-decoration: none;

      i {
        position: relative;
        top: 1px;
        font-size: $size-small;
      }

      &:before,
      &:after {
        display: none;
      }

      &:hover,
      &:focus,
      &:active {
        background-color: $white;
      }
    }

    &:not(.is-delete) {
      cursor: default;
    }
  }
}

.menu-list {
  a {
    display: flex;
    align-items: baseline;
    height: 2.875rem;
    padding: 0.875rem 1.5rem;
    color: rgba($black, 0.87);
    font-size: 0.9375rem;
    line-height: 1.125rem;
    transition: $transition;

    &.is-active {
      background-color: $white;
      cursor: default;

      .tag {
        background-color: $blue;
        color: $white;
        border-color: $blue;
      }
    }

    &:not(.is-active) {
      &:hover {
        background-color: hsl(0, 0, 91%);
      }
    }

    .tag {
      background-color: $white;
      color: rgba($black, 0.72);
      border: 1px solid rgba($black, 0.08);
      height: 1.125rem;
      padding-left: 0.5rem;
      padding-right: 0.5rem;
      margin-left: 0.5rem;
      transition: $transition;
    }
  }

  li {
    border-bottom: 1px solid rgba($black, 0.06);

    &:last-child {
      border-width: 0;
    }
  }
}
</style>
